import React, { useEffect, useState } from "react";
import { Autocomplete, Box, Button, Chip, ClickAwayListener, Grid, Grow, Paper, Popper, Stack, styled, Switch, TextField, Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import ScheduleStatisticsKpi from "./scheduleKpi";
import ActivitiesKpi from "./activitiesKpi";
import UpcomingMilestone from "./upcomingMilestone";
import SCurveSchedule from "./SCurvePhysical";
import SchedulePerformanceIndex from "./schedulePerformanceIndex";
import { useLazyGetScheduleKpisQuery, useLazyGetProjectSCurveQuery, useLazyGetWbsLevelsQuery, useGetWbsLevelsQuery } from "store/slices/apis/scheduleSlice";
import { useParams } from "react-router-dom";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import * as Yup from "yup";
import { Form, Formik } from "formik";
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import ScheduleGantt from "./ScheduleGantt";
import WbsTreeView from "../wbsTreeView";
import ProjectLastUpdatedOn from "components/ProjectLastUpdatedOn";
import { useGetLastUpdatedOnStatusQuery } from "store/slices/apis/projectStatusApiSlice";
import { enterpriseTokens } from "styles/theme";

const validationSchema = Yup.object({
    category: Yup.string().nullable(),
    subCategory: Yup.string().nullable(),
    wbsId: Yup.string().nullable(),
    wbsName: Yup.string().nullable(),
});

const AntSwitch = styled(Switch)(({ theme }) => ({
    width: 28,
    height: 16,
    padding: 0,
    display: 'flex',
    '&:active': {
        '& .MuiSwitch-thumb': {
            width: 15,
        },
        '& .MuiSwitch-switchBase.Mui-checked': {
            transform: 'translateX(9px)',
        },
    },
    '& .MuiSwitch-switchBase': {
        padding: 2,
        '&.Mui-checked': {
            transform: 'translateX(12px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
                opacity: 1,
                backgroundColor: '#1890ff',
                ...theme.applyStyles('dark', {
                    backgroundColor: '#177ddc',
                }),
            },
        },
    },
    '& .MuiSwitch-thumb': {
        boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
        width: 12,
        height: 12,
        borderRadius: 3,
        transition: theme.transitions.create(['width'], {
            duration: 200,
        }),
    },
    '& .MuiSwitch-track': {
        borderRadius: 16 / 2,
        opacity: 1,
        backgroundColor: 'rgba(0,0,0,.25)',
        boxSizing: 'border-box',
        ...theme.applyStyles('dark', {
            backgroundColor: 'rgba(255,255,255,.35)',
        }),
    },
}));

const ScheduleDashboard = () => {
    const { projectId } = useParams();

    const [isApplyFilter, setIsApplyFIlter] = React.useState(false);

    const [open, setOpen] = useState(false);
    const anchorRef = React.useRef(null);
    const arrowRef = React.useRef(null);

    const { data: scheduleData } = useGetLastUpdatedOnStatusQuery({ ProjectId: projectId });

    const [filters, setFilters] = useState({
        projectId: projectId,
        category: "",
        wbsName: "",
        subCategory: "",
        parentId: "",
        planWorld: "current"
    });


    const [trigger, { data, isLoading, isError, error }] = useLazyGetScheduleKpisQuery();
    const [sCurvetrigger, { data: sCurvedata, isLoading: isSCurveLoading, isSCurveError, sCurveerror, isSuccess: isCurveSuccess }] = useLazyGetProjectSCurveQuery();
    //const [gantTrigger, { data:gantData, isLoading:isGantLoading, isError:isGantError }] = useLazyGetWbsLevelsQuery();
    let parentId = null
    if (projectId === "7d9e0306-81f4-4cdc-8752-de441b460997") {
        parentId = 683;
    } else if (projectId === "d0e4e2da-2c8d-4127-9d67-eda7fe97d820") {
        parentId = 1
    }
    const { data: wbsTreeData } = useGetWbsLevelsQuery(
        filters.projectId ? { projectId: filters.projectId, parentId: parentId } : skipToken
    );

    useEffect(() => {
        trigger(filters);
        sCurvetrigger(filters);
        //gantTrigger(filters);
    }, []);

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const handleApplyFilters = async (values, { setSubmitting }) => {
        console.log("Apply filters →", values);

        const payload = {
            projectId: projectId,
            category: values.category || "",
            subCategory: values.wbsId || "",
            parentId: values.wbsId || "",
            activityId: values.wbsId || "",
            planWorld: values.planWorld || "current"
        };

        // keep name for showing in Chip / text
        const newFilters = {
            ...payload,
            wbsName: values.wbsName || ""
        };

        console.log("New filters →", newFilters);
        setFilters(newFilters);

        // call your APIs
        trigger(payload);
        sCurvetrigger(payload);
        //gantTrigger(payload);

        setIsApplyFIlter(true);
        setOpen(false);
        setSubmitting(false);
    };

    const handleReset = (resetForm) => {
        resetForm();
        resetAllFilters();
    };

    // Clears filters + reloads APIs
    const resetAllFilters = () => {
        const resetValues = {
            projectId,
            category: "",
            subCategory: "",
            activity: "",
            wbsId: "",
            wbsName: "",
            parentId: "",
            activityId: "",
            planWorld: "current"
        };

        // update local filters state
        setFilters(resetValues);

        // reload APIs with empty filters
        trigger(resetValues);
        sCurvetrigger(resetValues);
        //gantTrigger(resetValues);

        setIsApplyFIlter(false);
        setOpen(false);
    };


    const handleDelete = (fieldName) => {
        setFilters(prev => ({
            ...prev,
            [fieldName]: ""
        }));

        const updatedFilters = {
            ...filters,
            [fieldName]: "",
        };
        trigger(updatedFilters);
        sCurvetrigger(updatedFilters);
        //gantTrigger(updatedFilters);

    }

    if (isLoading) {
        return (
            <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
                <Typography color="error">
                    Loading KPIs...
                </Typography>
            </Box>
        );
    }
    if (isError) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography color="error">
                    Failed to load Schedule KPIs.
                </Typography>
                {/* Uncomment if you want more detail */}
                {/* <Typography variant="body2">{JSON.stringify(error)}</Typography> */}
            </Box>
        );
    }
    return (
        <>
            <Grid container spacing={1.5} sx={{ p: { xs: 1.5, md: 2 } }}>

                <Grid size={12}>
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: "6px",
                    }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: "18px", md: "20px" },
                                    lineHeight: { xs: "24px", md: "26px" },
                                    color: "#252C32",
                                    fontFamily: "Outfit, sans-serif",
                                }}
                            >
                                Schedule Delivery
                            </Typography>
                            <Typography
                                sx={{
                                    fontWeight: 400,
                                    fontSize: '12px',
                                    lineHeight: "18px",
                                    color: enterpriseTokens.muted,
                                    fontFamily: "Outfit, sans-serif",
                                }}
                            >
                                Monitor progress, milestone readiness, and emerging delay exposure against plan.
                            </Typography>
                        </Box>
                        <Stack alignItems="flex-end">
                            <Stack direction="row" spacing={2} alignItems="center">
                                {filters.wbsName && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: "#0E73F6" }}>Filter:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {filters.wbsName}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={resetAllFilters}
                                    />
                                )}

                                <Button
                                    variant="outlined"
                                    startIcon={<FilterAltOutlinedIcon sx={{ fontSize: "18px !important" }} />}
                                    aria-label="Open schedule filters"
                                    ref={anchorRef}
                                    onClick={handleToggle}
                                    sx={{
                                        minHeight: 34,
                                        px: 1.5,
                                        borderColor: "#D8E0EA",
                                        color: "#46566F",
                                        backgroundColor: open ? "#EDF4FF" : "#FFFFFF",
                                        "&:hover": { borderColor: "#94A3B8", backgroundColor: "#F4F7FB" },
                                    }}
                                >
                                    Filters
                                </Button>
                                <Popper
                                    open={open}
                                    anchorEl={anchorRef.current}
                                    placement="bottom-end"
                                    transition
                                    disablePortal={false}
                                    // sx={{ zIndex: 4000 }}
                                    onClick={(e) => e.stopPropagation()}
                                    modifiers={[
                                        {
                                            name: 'arrow',
                                            enabled: true,
                                            options: {
                                                element: arrowRef?.current,
                                            },
                                        },
                                    ]}
                                >
                                    {({ TransitionProps }) => (
                                        <Grow {...TransitionProps}>
                                            <Paper
                                                sx={{
                                                    p: 2,
                                                    // borderRadius: 1,
                                                    boxShadow: "none",
                                                    width: 300,
                                                    borderRadius: 3,
                                                    border: "2px solid #eef0f4",
                                                    position: 'relative'
                                                }}
                                            >
                                                <ArrowDropUpRoundedIcon
                                                    ref={arrowRef}
                                                    sx={{
                                                        position: "absolute",
                                                        top: "-33px",
                                                        right: "0px",
                                                        fontSize: "60px",
                                                        color: "white",
                                                        filter: "drop-shadow(0px -1px 1px rgba(0,0,0,0.15))",
                                                    }}
                                                />
                                                <ClickAwayListener onClickAway={handleClose}>
                                                    <Box>
                                                        {open && (

                                                            <Formik
                                                                initialValues={filters}
                                                                validationSchema={validationSchema}
                                                                onSubmit={handleApplyFilters}
                                                                enableReinitialize
                                                            >
                                                                {({ values,
                                                                    errors,
                                                                    touched,
                                                                    handleChange,
                                                                    handleBlur,
                                                                    handleSubmit,
                                                                    isSubmitting,
                                                                    setFieldValue,
                                                                    resetForm,
                                                                    setFieldTouched,
                                                                    setTouched }) => {
                                                                    // const subCatOptions = values?.category
                                                                    //     ? subCategoryMap[values?.category]
                                                                    //     : [];

                                                                    return (
                                                                        <Form>
                                                                            <Grid container spacing={2}>
                                                                                {/* ... your Category + Sub Category stuff ... */}

                                                                                {/* WBS TREE SECTION */}
                                                                                <Grid size={{ xs: 12, md: 12 }}>
                                                                                    <Typography sx={{ mb: 1, mt: 1 }}>
                                                                                        WBS Selector
                                                                                    </Typography>

                                                                                    <WbsTreeView
                                                                                        projectId={projectId}
                                                                                        onSelect={function (node) {
                                                                                            // node = { id, name, raw, isLeaf }

                                                                                            // Save into form values
                                                                                            setFieldValue("wbsId", node.id);
                                                                                            setFieldValue("wbsName", node.name);
                                                                                        }}
                                                                                    />

                                                                                    {/* Optional: show selected WBS name inside popup */}
                                                                                    {values.wbsName && (
                                                                                        <Typography sx={{ mt: 1, fontSize: 12, color: "#555" }}>
                                                                                            Selected WBS: <strong>{values.wbsName}</strong>
                                                                                        </Typography>
                                                                                    )}
                                                                                </Grid>
                                                                                {/* <Grid size={12}>
                                                                                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                                                                    <Typography>Baseline</Typography>
                                                                                    <AntSwitch checked={values?.planWorld === "current"}
                                                                                        onChange={(event) => {
                                                                                            const checked = event.target.checked;
                                                                                            setFieldValue("planWorld", checked ? "current" : "baseline");
                                                                                        }}
                                                                                        inputProps={{ 'aria-label': 'ant design' }} />
                                                                                    <Typography>Current</Typography>
                                                                                </Stack>
                                                                            </Grid> */}
                                                                                {/* Apply */}
                                                                                <Grid size={12}>
                                                                                    <Box
                                                                                        sx={{
                                                                                            display: "flex",
                                                                                            justifyContent: "flex-end",
                                                                                            alignItems: "center"
                                                                                        }}
                                                                                    >
                                                                                        {isApplyFilter && (
                                                                                            <Button
                                                                                                onClick={function () {
                                                                                                    handleReset(resetForm);
                                                                                                }}
                                                                                                sx={{
                                                                                                    textTransform: "none",
                                                                                                    color: "#F15744",
                                                                                                    fontSize: "12px",
                                                                                                    lineHeight: "100%",
                                                                                                    fontFamily: "Outfit, sans-serif",
                                                                                                    borderRadius: "25px",
                                                                                                    fontWeight: 500,
                                                                                                    px: "20px",
                                                                                                    py: "15px",
                                                                                                }}
                                                                                            >
                                                                                                Reset
                                                                                            </Button>
                                                                                        )}

                                                                                        <Button
                                                                                            type="submit"
                                                                                            variant="contained"
                                                                                            disabled={isSubmitting}
                                                                                            sx={{
                                                                                                backgroundColor: "#00344C",
                                                                                                borderRadius: "25px",
                                                                                                textTransform: "none",
                                                                                                fontWeight: 500,
                                                                                                fontSize: "12px",
                                                                                                lineHeight: "100%",
                                                                                                fontFamily: "Outfit, sans-serif",
                                                                                                color: "#FFFFFF",
                                                                                                px: "20px",
                                                                                                py: "15px",
                                                                                            }}
                                                                                        >
                                                                                            Apply
                                                                                        </Button>
                                                                                    </Box>
                                                                                </Grid>
                                                                            </Grid>

                                                                            {/* <Grid container spacing={2}>
                                                                            <Grid size={{ xs: 12, md: 12 }}>
                                                                                <Typography sx={{ mb: 1, mt: 1 }}>
                                                                                    Category
                                                                                </Typography>
                                                                                <Autocomplete
                                                                                    options={categoryOptions}
                                                                                    loading={isLoadingCategories}
                                                                                    getOptionLabel={(option) => option?.label || ''}
                                                                                    value={
                                                                                        categoryOptions?.find(
                                                                                            (opt) => opt.value == values?.category
                                                                                        ) || null
                                                                                    }
                                                                                    onChange={(e, newValue) => {
                                                                                        const id = newValue?.value ?? null;
                                                                                        setFieldValue("category", id);
                                                                                        setFieldValue("subCategory", "");
                                                                                        setSelectedCategoryId(id || null);
                                                                                    }}
                                                                                    renderInput={(params) => (
                                                                                        <TextField
                                                                                            {...params}
                                                                                            placeholder="Select Category"
                                                                                            name="category"
                                                                                            size="small"
                                                                                            sx={{
                                                                                                "& input::placeholder": {
                                                                                                    fontWeight: 400,
                                                                                                    fontSize: "13px",
                                                                                                    lineHeight: "100%",
                                                                                                    letterSpacing: "0.16px",
                                                                                                    color: "#5F5F5F",
                                                                                                    fontFamily: "Outfit, sans-serif",
                                                                                                    opacity: 1,
                                                                                                },
                                                                                            }}
                                                                                        />
                                                                                    )}
                                                                                    ListboxProps={{ sx: { fontSize: '13px' } }}
                                                                                />
                                                                                {touched?.category && errors?.category && (
                                                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                                                        {errors?.category}
                                                                                    </Typography>
                                                                                )}
                                                                            </Grid>
                                                                            <Grid size={{ xs: 12, md: 12 }}>
                                                                                <Typography sx={{ mb: 1, mt: 1 }}>
                                                                                    Sub Category
                                                                                </Typography>
                                                                                <Autocomplete
                                                                                    options={subCatOptions}
                                                                                    loading={isLoadingSubCategories}
                                                                                    getOptionLabel={(option) => option?.label || ''}
                                                                                    value={
                                                                                        subCatOptions?.find(
                                                                                            (opt) => opt.value == values?.subCategory
                                                                                        ) || null
                                                                                    }
                                                                                    onChange={(e, newValue) =>{
                                                                                        const id = newValue?.value ?? "";

                                                                                        setFieldValue("subCategory", id);

                                                                                        // 🔥 This triggers Level 3 API call
                                                                                        setSelectedSubCategoryId(id || null);
                                                                                    }}
                                                                                    disabled={!values.category}
                                                                                    renderInput={(params) => (
                                                                                        <TextField
                                                                                            {...params}
                                                                                            placeholder="Sub Category"
                                                                                            name={`status`}
                                                                                            size="small"
                                                                                            sx={{
                                                                                                "& input::placeholder": {
                                                                                                    fontWeight: 400,
                                                                                                    fontSize: "13px",
                                                                                                    lineHeight: "100%",
                                                                                                    letterSpacing: "0.16px",
                                                                                                    color: "#5F5F5F",
                                                                                                    fontFamily: "Outfit, sans-serif",
                                                                                                    opacity: 1,
                                                                                                },
                                                                                            }}
                                                                                        />
                                                                                    )}
                                                                                    ListboxProps={{ sx: { fontSize: '13px' } }}
                                                                                />
                                                                                {touched?.subCategory && errors?.subCategory && (
                                                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                                                        {errors?.subCategory}
                                                                                    </Typography>
                                                                                )}
                                                                            </Grid>
                                                                            <Grid size={{ xs: 12, md: 12 }}>
                                                                                <Typography sx={{ mb: 1, mt: 1 }}>
                                                                                    Activity
                                                                                </Typography>

                                                                                <Autocomplete
                                                                                    options={level3Options}
                                                                                    loading={isLoadingLevel3}
                                                                                    getOptionLabel={(option) => option?.label || ""}
                                                                                    value={
                                                                                    level3Options.find(
                                                                                        (opt) => opt.value === values.activity
                                                                                    ) || null
                                                                                    }
                                                                                    onChange={(e, newValue) =>
                                                                                    setFieldValue("activity", newValue?.value ?? "")
                                                                                    }
                                                                                    disabled={!values.subCategory}
                                                                                    renderInput={(params) => (
                                                                                    <TextField
                                                                                        {...params}
                                                                                        placeholder="Select Activity"
                                                                                        name="activity"
                                                                                        size="small"
                                                                                        sx={{
                                                                                        "& input::placeholder": {
                                                                                            fontWeight: 400,
                                                                                            fontSize: "13px",
                                                                                            lineHeight: "100%",
                                                                                            letterSpacing: "0.16px",
                                                                                            color: "#5F5F5F",
                                                                                            fontFamily: "Outfit, sans-serif",
                                                                                            opacity: 1,
                                                                                        },
                                                                                        }}
                                                                                    />
                                                                                    )}
                                                                                    ListboxProps={{ sx: { fontSize: "13px" } }}
                                                                                />
                                                                                </Grid>
                                                                            <Grid size={12}>
                                                                                <Box
                                                                                    sx={{
                                                                                        display: "flex",
                                                                                        justifyContent: "flex-end",
                                                                                        alignItems: "center",
                                                                                    }}
                                                                                >
                                                                                    {isApplyFilter &&
                                                                                        <Button onClick={handleReset}
                                                                                            sx={{
                                                                                                textTransform: "none",
                                                                                                color: "#F15744",
                                                                                                fontSize: "12px",
                                                                                                lineHeight: "100%",
                                                                                                fontFamily: "Outfit, sans-serif",
                                                                                                borderRadius: "25px",
                                                                                                fontWeight: 500,
                                                                                                px: "20px",
                                                                                                py: "15px",
                                                                                            }}>
                                                                                            Reset
                                                                                        </Button>
                                                                                    }
                                                                                    <Button
                                                                                        type="submit"
                                                                                        variant="contained"
                                                                                        // loading={addActionTrackerRes?.isLoading}
                                                                                        sx={{
                                                                                            backgroundColor: "#00344C",
                                                                                            borderRadius: "25px",
                                                                                            textTransform: "none",
                                                                                            fontWeight: 500,
                                                                                            fontSize: "12px",
                                                                                            lineHeight: "100%",
                                                                                            fontFamily: "Outfit, sans-serif",
                                                                                            color: "#FFFFFF",
                                                                                            px: "20px",
                                                                                            py: "15px",
                                                                                        }}
                                                                                    >
                                                                                        Apply
                                                                                    </Button>
                                                                                </Box>
                                                                            </Grid>
                                                                        </Grid> */}
                                                                        </Form>
                                                                    );
                                                                }}
                                                            </Formik>

                                                        )}
                                                    </Box>
                                                </ClickAwayListener>
                                            </Paper>
                                        </Grow>
                                    )}
                                </Popper>
                            </Stack>
                        </Stack>
                    </Box>
                </Grid>
                <Grid size={12}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            pb: 0.5,
                            borderBottom: `1px solid ${enterpriseTokens.lineSoft}`,
                            "& .MuiTypography-root": { fontSize: "12px", color: enterpriseTokens.muted },
                        }}
                    >
                        <ProjectLastUpdatedOn date={scheduleData?.scheduleUpdatedDate} />
                    </Box>
                </Grid>
                <Grid container spacing={1.5}>
                    <Grid size={{ md: 12 }}>
                        {/* <Stack direction="row" spacing={1}>
                            {filters?.category &&
                                <Chip
                                    label={
                                        <Typography variant="body2">
                                            <strong style={{ color: '#0E73F6' }}>Category:</strong>{" "}
                                            <span style={{ fontWeight: 400, color: "#6B7280" }}>{filters?.category}</span>
                                        </Typography>
                                    }
                                    sx={{
                                        backgroundColor: "rgba(25, 118, 210, 0.08)",
                                        border: 0
                                    }}
                                    variant="outlined"
                                    onDelete={() => handleDelete('category')}
                                />
                            }
                            {filters?.subCategory &&
                                <Chip
                                    label={
                                        <Typography variant="body2">
                                            <strong style={{ color: '#0E73F6' }}>Sub Category:</strong>{" "}
                                            <span style={{ fontWeight: 400, color: "#6B7280" }}>{filters?.subCategory}</span>
                                        </Typography>
                                    }
                                    sx={{
                                        backgroundColor: "rgba(25, 118, 210, 0.08)",
                                        border: 0
                                    }}
                                    variant="outlined"
                                    onDelete={() => handleDelete('subCategory')}
                                />
                            }
                        </Stack> */}
                        <Box sx={{
                            background: "#fff",
                            borderRadius: "3px",
                            mt: 1
                            // p: 2,
                            // boxShadow: "0px 4px 20px rgba(0,0,0,0.05)"
                        }}>
                            <ScheduleStatisticsKpi kpiData={data} />
                        </Box>
                    </Grid>
                    <Grid size={{ md: 7 }}>
                        <SCurveSchedule kpiData={sCurvedata} scheduleData={data} isCurveSuccess={isCurveSuccess} />
                    </Grid>
                    <Grid size={{ md: 5 }}>
                        <Grid container spacing={1.5} direction="column">
                            <Grid size={{ xs: 12 }}>
                                <SchedulePerformanceIndex kpiData={data} />
                            </Grid>
                            <Grid size={{ xs: 12 }} >
                                <ActivitiesKpi kpiData={data} category={filters?.subCategory} />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid size={{ xs: 12, md: 12 }}>
                        <ScheduleGantt kpiData={wbsTreeData} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 12 }}>
                        <UpcomingMilestone />
                    </Grid>
                    {/* <Grid size={{ xs: 12, md: 5 }}>
                        <ActivitiesKpi />
                    </Grid> */}


                    {/* <Grid size={{ md: 5 }}>
                        <Grid container spacing={1.5} direction="column">
                            <Grid size={{ xs: 12 }}>
                                <SchedulePerformanceIndex />
                            </Grid>
                            <Grid size={{ xs: 12 }} >
                                <CompletionTillDate />
                            </Grid>
                        </Grid> 
                    </Grid> */}
                </Grid>

            </Grid >
        </>
    );
};

export default ScheduleDashboard;
